import { Hash, Search, X } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import type { SearchType } from '@/api/types'
import { Avatar } from '@/components/ui/Avatar'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'

import { SEARCH_TYPES, useTypeahead } from '../hooks/useSearch'

/**
 * 搜索框 + 下拉提示。
 *
 * 提示只是快捷入口：直接回车走全文搜索，不强迫从提示里选。
 */
export function SearchBox({
  initial = '',
  autoFocus = false,
}: {
  initial?: string
  autoFocus?: boolean
}) {
  const t = useT()
  const [value, setValue] = useState(initial)
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  // 右栏与搜索页可能同时渲染一个搜索框，写死 id 会导致重复 id、label 失联
  const inputId = useId()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { data } = useTypeahead(value)

  useEffect(() => setValue(initial), [initial])

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [])

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    const q = value.trim()
    if (!q) return
    setOpen(false)
    // 已经在搜索页时保留当前 tab：在「用户」tab 下再搜一次不该被踢回「帖子」
    const type = params.get('type')
    const keepType = type && SEARCH_TYPES.includes(type as SearchType) ? `&type=${type}` : ''
    navigate(`/search?q=${encodeURIComponent(q)}${keepType}`)
  }

  const go = (to: string) => {
    setOpen(false)
    navigate(to)
  }

  const hasHints = Boolean(data && (data.users.length > 0 || data.hashtags.length > 0))

  return (
    <div ref={rootRef} className="relative">
      <form role="search" onSubmit={submit}>
        <label htmlFor={inputId} className="sr-only">
          {t('search.action')}
        </label>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-ink-muted" />
          <input
            id={inputId}
            type="search"
            value={value}
            autoFocus={autoFocus}
            onChange={(event) => {
              setValue(event.target.value)
              setOpen(true)
            }}
            onFocus={() => setOpen(true)}
            placeholder={t('search.placeholder')}
            className={cn(
              'w-full rounded-md bg-surface-subtle py-3 pl-11 pr-10 text-[15px]',
              'outline-none placeholder:text-ink-muted',
              'focus:bg-surface focus:ring-1 focus:ring-brand-ink',
            )}
          />
          {value && (
            <button
              type="button"
              onClick={() => {
                setValue('')
                setOpen(false)
              }}
              aria-label={t('search.clear')}
              className="absolute right-3 top-1/2 -translate-y-1/2 px-corners [--px-step:1px] bg-brand p-0.5 text-on-brand"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
      </form>

      {open && hasHints && (
        <div className="absolute inset-x-0 z-30 mt-1 overflow-hidden rounded-md border border-line bg-surface shadow-lg">
          {data?.users.map((user) => (
            <button
              key={user.id}
              type="button"
              onClick={() => go(`/${user.username}`)}
              className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-surface-hover"
            >
              <Avatar src={user.avatar_url} alt={user.display_name} size="xs" />
              <span className="min-w-0">
                <span className="block truncate text-[15px] font-bold">{user.display_name}</span>
                <span className="block truncate text-[13px] text-ink-muted">@{user.username}</span>
              </span>
            </button>
          ))}

          {data?.hashtags.map((tag) => (
            <button
              key={tag.name}
              type="button"
              onClick={() => go(`/hashtag/${encodeURIComponent(tag.name)}`)}
              className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-surface-hover"
            >
              <Hash className="size-5 text-ink-muted" />
              <span className="min-w-0">
                <span className="block truncate text-[15px] font-bold">#{tag.display}</span>
                <span className="block text-[13px] text-ink-muted">
                  {t('search.tagPosts', { count: tag.post_count })}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
